import { generatePagerProps } from '@fukuri-admins/utils/common/generatePagerProps';
import { useUserList } from './hooks';
import { convertToPresentationProps } from './logics';
import { UserListPresentation } from './presentations';
import { UserListEmptyPresentation } from './presentations/empty';

/**
 * 利用者一覧 Container
 *
 * - 状態管理・アクションは useUserList に委譲する
 * - API 型 → Presentation 型の変換は convertToPresentationProps に委譲する
 * - Empty / 通常表示の出し分けは isEmpty フラグで行う（三項演算子）
 * - 検索条件あり × 0 件のときは Empty を表示せず検索フォーム＋テーブルを表示する（PEN-309 対応パターン）
 * - アクティベーションコード UI の表示可否（isActivationCodeEnabled）は convertToPresentationProps が
 *   enableStaging・PartnerFeatureFlag から導出する。Container は useUserList の state
 *   （enableStaging / partnerFeatureFlagsData）を渡すだけ（MMT-156 リファクタ）
 * - 「電話番号」列の表示可否は convertToPresentationProps が login_method から導出する。
 *   isActivationCodeEnabled には連動しない（MMT-290）
 * - 「メールアドレス」列は login_method に関わらず常時表示する（MMT-296）
 * - 電話番号の検索欄も showPhoneNumberColumn で出し分ける（列と検索欄を一致させる。MMT-294）
 *
 * NOTE: 親コンポーネント（Screen）で Suspense にラップすること
 */
export const UserList: React.FC = () => {
  const {
    state: {
      usersData,
      totalPage,
      totalCount,
      currentPage,
      fullNameValue,
      emailValue,
      clientNameValue,
      phoneNumberValue,
      activationCodeValue,
      isFiltered,
      enableStaging,
      partnerFeatureFlagsData,
      applicationConfiguration,
    },
    actions: {
      handleClickAddUserByEmail,
      handleClickAddUserByActivationCode,
      handlePageChange,
      handleRowClick,
      handleDeleteUser,
      handleEditUser,
      handleCopyActivationCode,
      handleFilterChange,
      handleSearch,
      handleResetFilters,
    },
  } = useUserList();

  const { rows, isEmpty, isActivationCodeEnabled, showPhoneNumberColumn } =
    convertToPresentationProps({
      usersData,
      isFiltered,
      enableStaging,
      partnerFeatureFlagsData,
      applicationConfiguration,
    });

  const pagerProps = generatePagerProps({
    currentPage,
    totalPage,
    totalCount,
    rows,
    onPageChange: handlePageChange,
  });

  return isEmpty ? (
    <UserListEmptyPresentation
      isActivationCodeEnabled={isActivationCodeEnabled}
      onClickAddUserByEmail={handleClickAddUserByEmail}
      onClickAddUserByActivationCode={handleClickAddUserByActivationCode}
    />
  ) : (
    <UserListPresentation
      rows={rows}
      isActivationCodeEnabled={isActivationCodeEnabled}
      onEditUser={handleEditUser}
      onDeleteUser={handleDeleteUser}
      onRowClick={handleRowClick}
      pagerProps={pagerProps}
      onClickAddUserByEmail={handleClickAddUserByEmail}
      onClickAddUserByActivationCode={handleClickAddUserByActivationCode}
      fullNameValue={fullNameValue}
      emailValue={emailValue}
      clientNameValue={clientNameValue}
      phoneNumberValue={phoneNumberValue}
      activationCodeValue={activationCodeValue}
      onFullNameChange={handleFilterChange('full_name')}
      onEmailChange={handleFilterChange('email')}
      onClientNameChange={handleFilterChange('client_name')}
      onPhoneNumberChange={handleFilterChange('phone_number')}
      onActivationCodeChange={handleFilterChange('activation_code')}
      onSearch={handleSearch}
      onReset={handleResetFilters}
      onCopyActivationCode={handleCopyActivationCode}
      showPhoneNumberColumn={showPhoneNumberColumn}
    />
  );
};
